import React, { useState, useRef, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { notepadStore } from '../stores/notepad-store';
import './notepad-ui.scss';

export const NotepadUI = observer(() => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreatingNote, setIsCreatingNote] = useState(false);
  const [newNoteTitle, setNewNoteTitle] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  const filteredNotes = searchQuery 
    ? notepadStore.searchNotes(searchQuery)
    : notepadStore.sortedNotes;

  const handleCreateNote = () => {
    if (newNoteTitle.trim()) {
      notepadStore.createNote(newNoteTitle.trim());
      setNewNoteTitle('');
      setIsCreatingNote(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      action();
    }
    if (e.key === 'Escape') {
      setIsCreatingNote(false);
      setNewNoteTitle('');
    }
  };

  const handleContentChange = (content: string) => {
    if (notepadStore.activeNote) {
      notepadStore.updateNoteContent(notepadStore.activeNote.id, content);
    }
  };

  const handleTitleChange = (title: string) => {
    if (notepadStore.activeNote) {
      notepadStore.updateNoteTitle(notepadStore.activeNote.id, title);
    }
  };

  const handleExportNote = () => {
    if (notepadStore.activeNote) {
      const exportData = notepadStore.exportNote(notepadStore.activeNote.id);
      if (exportData) {
        const blob = new Blob([exportData], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${notepadStore.activeNote.title}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  useEffect(() => {
    if (isCreatingNote && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [isCreatingNote]);

  useEffect(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }, [notepadStore.activeNote?.content]);

  if (notepadStore.isLoading) {
    return (
      <div className="notepad">
        <div className="notepad__loading">Loading notes...</div>
      </div>
    );
  }

  return (
    <div className="notepad">
      <div className="notepad__sidebar">
        <div className="notepad__sidebar-header">
          <div className="notepad__search">
            <input
              type="text"
              placeholder="Search notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="notepad__search-input"
            />
          </div>
          
          <div className="notepad__create-section">
            {isCreatingNote ? (
              <div className="notepad__create-form">
                <input
                  ref={titleInputRef}
                  type="text"
                  placeholder="Note title..."
                  value={newNoteTitle}
                  onChange={(e) => setNewNoteTitle(e.target.value)}
                  onKeyDown={(e) => handleKeyPress(e, handleCreateNote)}
                  className="notepad__create-input"
                />
                <div className="notepad__create-actions">
                  <button 
                    onClick={handleCreateNote}
                    disabled={!newNoteTitle.trim()}
                    className="notepad__btn notepad__btn--primary"
                  >
                    Create
                  </button>
                  <button 
                    onClick={() => {
                      setIsCreatingNote(false);
                      setNewNoteTitle('');
                    }}
                    className="notepad__btn notepad__btn--secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button 
                onClick={() => setIsCreatingNote(true)}
                className="notepad__btn notepad__btn--create"
              >
                + New Note
              </button>
            )}
          </div>
        </div>

        <div className="notepad__notes-list">
          {filteredNotes.length === 0 ? (
            <div className="notepad__empty">
              {searchQuery ? 'No notes found' : 'No notes yet'}
            </div>
          ) : (
            filteredNotes.map((note) => (
              <div
                key={note.id}
                onClick={() => notepadStore.selectNote(note.id)}
                className={`notepad__note-item ${
                  notepadStore.activeNoteId === note.id ? 'notepad__note-item--active' : ''
                }`}
              >
                <div className="notepad__note-title">{note.title}</div>
                <div className="notepad__note-meta">
                  <span className="notepad__note-date">
                    {formatDate(note.modifiedAt)}
                  </span>
                  <span className="notepad__note-content-preview">
                    {note.content.substring(0, 50)}
                    {note.content.length > 50 ? '...' : ''}
                  </span>
                </div>
                
                <div className="notepad__note-actions">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      notepadStore.duplicateNote(note.id);
                    }}
                    className="notepad__note-action"
                    title="Duplicate note"
                  >
                    📋
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm('Delete this note?')) {
                        notepadStore.deleteNote(note.id);
                      }
                    }}
                    className="notepad__note-action notepad__note-action--delete"
                    title="Delete note"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="notepad__editor">
        {notepadStore.activeNote ? (
          <>
            <div className="notepad__editor-header">
              <input
                type="text"
                value={notepadStore.activeNote.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                className="notepad__title-input"
                placeholder="Note title..."
              />
              
              <div className="notepad__editor-actions">
                <button
                  onClick={handleExportNote}
                  className="notepad__btn notepad__btn--secondary"
                  title="Export note"
                >
                  📤 Export
                </button>
                <button
                  onClick={() => {
                    if (notepadStore.activeNote) {
                      notepadStore.duplicateNote(notepadStore.activeNote.id);
                    }
                  }}
                  className="notepad__btn notepad__btn--secondary"
                  title="Duplicate note"
                >
                  📋 Duplicate
                </button>
                <button
                  onClick={() => {
                    if (notepadStore.activeNote && confirm('Delete this note?')) {
                      notepadStore.deleteNote(notepadStore.activeNote.id);
                    }
                  }}
                  className="notepad__btn notepad__btn--danger"
                  title="Delete note"
                >
                  🗑️ Delete
                </button>
              </div>
            </div>

            <div className="notepad__editor-meta">
              <span>Created: {formatDate(notepadStore.activeNote.createdAt)}</span>
              <span>Modified: {formatDate(notepadStore.activeNote.modifiedAt)}</span>
              <span>{notepadStore.activeNote.content.length} characters</span>
            </div>

            <textarea
              ref={textareaRef}
              value={notepadStore.activeNote.content}
              onChange={(e) => handleContentChange(e.target.value)}
              placeholder="Start writing your note..."
              className="notepad__content-textarea"
            />
          </>
        ) : (
          <div className="notepad__no-selection">
            <div className="notepad__no-selection-content">
              <h3>Select a note to start editing</h3>
              <p>Choose a note from the sidebar or create a new one to begin.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});
